/**
 * Avatar Module
 * Handles all avatar-related operations including upload, update, removal, and serving
 */

import { BaseModule } from './baseModule';
import type {
  AvatarUploadResponse,
  AvatarUpdateRequest,
  MessageResponse,
  ProgressCallback,
} from './types';

export class AvatarModule extends BaseModule {
  /**
   * Upload a new avatar image
   * POST /api/user/avatar
   * 
   * @param file - The avatar image file to upload
   * @param onProgress - Optional callback for upload progress tracking
   * @returns Avatar upload response with URL and message
   */
  async uploadAvatar(
    file: File,
    onProgress?: ProgressCallback
  ): Promise<AvatarUploadResponse> {
    // Validate file before upload
    this.validateAvatarFile(file);

    const formData = new FormData();
    formData.append('avatar', file);

    return this.post<AvatarUploadResponse>('/api/user/avatar', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      onUploadProgress: onProgress
        ? (progressEvent) => {
            if (progressEvent.total) {
              const progress = (progressEvent.loaded / progressEvent.total) * 100;
              onProgress(progress);
            }
          }
        : undefined,
    });
  }

  /**
   * Update avatar URL (for external images like Google profile pictures)
   * PUT /api/user/avatar
   * 
   * This endpoint downloads the external image and stores it locally
   * 
   * @param avatarUrl - External URL of the avatar image
   * @returns Avatar upload response with local URL and message
   */
  async updateAvatarUrl(avatarUrl: string): Promise<AvatarUploadResponse> {
    if (!avatarUrl || !this.isValidUrl(avatarUrl)) {
      throw new Error('Invalid avatar URL provided');
    }

    const request: AvatarUpdateRequest = {
      avatar_url: avatarUrl,
    };

    return this.put<AvatarUploadResponse>('/api/user/avatar', request);
  }

  /**
   * Remove current avatar
   * DELETE /api/user/avatar
   * 
   * Removes the user's avatar from both database and file system
   * 
   * @returns Message response confirming removal
   */
  async removeAvatar(): Promise<MessageResponse> {
    return this.delete<MessageResponse>('/api/user/avatar');
  }

  /**
   * Get avatar file
   * GET /api/avatars/:filename
   * 
   * Serves the avatar file with proper caching headers
   * 
   * @param filename - The avatar filename to retrieve
   * @returns Blob containing the avatar image
   */
  async getAvatar(filename: string): Promise<Blob> {
    if (!filename) {
      throw new Error('Avatar filename is required');
    }

    // Sanitize filename to prevent path traversal
    const safeFilename = this.sanitizeFilename(filename);

    return this.get<Blob>(`/api/avatars/${safeFilename}`, {
      responseType: 'blob',
    });
  }

  /**
   * Get avatar URL with cache busting
   * 
   * Adds a timestamp parameter to local avatars to force cache refresh
   * 
   * @param baseUrl - The base avatar URL
   * @returns URL with cache busting parameter if local, otherwise original URL
   */
  getAvatarUrl(baseUrl: string): string {
    if (!baseUrl) return '';

    // Add cache busting parameter for local avatars
    if (this.isLocalAvatar(baseUrl)) {
      const separator = baseUrl.includes('?') ? '&' : '?';
      return `${baseUrl}${separator}t=${Date.now()}`;
    }

    return baseUrl;
  }

  /**
   * Resize image before upload (client-side optimization)
   * 
   * Reduces image dimensions and file size before uploading
   * 
   * @param file - The image file to resize
   * @param maxWidth - Maximum width in pixels (default: 400)
   * @param maxHeight - Maximum height in pixels (default: 400)
   * @param quality - JPEG quality 0-1 (default: 0.8)
   * @returns Promise resolving to resized image blob
   */
  async resizeImage(
    file: File,
    maxWidth: number = 400,
    maxHeight: number = 400,
    quality: number = 0.8
  ): Promise<Blob> {
    return new Promise((resolve, reject) => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const img = new Image();

      img.onload = () => {
        // Calculate new dimensions maintaining aspect ratio
        let { width, height } = img;

        if (width > height) {
          if (width > maxWidth) {
            height = (height * maxWidth) / width;
            width = maxWidth;
          }
        } else {
          if (height > maxHeight) {
            width = (width * maxHeight) / height;
            height = maxHeight;
          }
        }

        // Set canvas dimensions
        canvas.width = width;
        canvas.height = height;

        // Draw and compress
        ctx?.drawImage(img, 0, 0, width, height);

        canvas.toBlob(
          (blob) => {
            if (blob) {
              resolve(blob);
            } else {
              reject(new Error('Failed to resize image'));
            }
          },
          file.type,
          quality
        );
      };

      img.onerror = () => reject(new Error('Failed to load image'));
      img.src = URL.createObjectURL(file);
    });
  }

  /**
   * Upload avatar with automatic resizing
   * 
   * Convenience method that resizes the image before uploading
   * 
   * @param file - The avatar image file
   * @param onProgress - Optional progress callback
   * @returns Avatar upload response
   */
  async uploadAvatarWithResize(
    file: File,
    onProgress?: ProgressCallback
  ): Promise<AvatarUploadResponse> {
    try {
      // Resize image before upload
      const resizedBlob = await this.resizeImage(file);
      
      // Convert blob back to file
      const resizedFile = new File([resizedBlob], file.name, {
        type: file.type,
        lastModified: Date.now(),
      });

      return this.uploadAvatar(resizedFile, onProgress);
    } catch (error) {
      // If resize fails, upload original
      console.warn('Image resize failed, uploading original:', error);
      return this.uploadAvatar(file, onProgress);
    }
  }

  // ============================================================================
  // Private Helper Methods
  // ============================================================================

  /**
   * Validate avatar file type and size
   * 
   * @param file - File to validate
   * @throws Error if validation fails
   */
  private validateAvatarFile(file: File): void {
    // Check file type
    const allowedTypes = [
      'image/jpeg',
      'image/jpg',
      'image/png',
      'image/gif',
      'image/webp',
    ];

    if (!allowedTypes.includes(file.type)) {
      throw new Error(
        'Invalid file type. Please select a valid image file (JPEG, PNG, GIF, or WebP)'
      );
    }

    // Check file size (5MB limit)
    const maxSize = 5 * 1024 * 1024; // 5MB
    if (file.size > maxSize) {
      throw new Error('Image file must be smaller than 5MB');
    }
  }

  /**
   * Check if avatar URL is stored locally
   * 
   * @param url - URL to check
   * @returns True if local, false if external
   */
  private isLocalAvatar(url: string): boolean {
    if (!url) return false;

    try {
      const urlObj = new URL(url, window.location.origin);
      const currentHost = window.location.hostname;

      return (
        urlObj.hostname === currentHost ||
        url.startsWith('/') ||
        url.startsWith('./')
      );
    } catch {
      // If URL parsing fails, assume it's a relative path (local)
      return true;
    }
  }

  /**
   * Validate URL format
   * 
   * @param url - URL to validate
   * @returns True if valid URL
   */
  private isValidUrl(url: string): boolean {
    try {
      new URL(url);
      return true;
    } catch {
      return false;
    }
  }

  /**
   * Sanitize filename to prevent path traversal attacks
   * 
   * @param filename - Filename to sanitize
   * @returns Sanitized filename
   */
  private sanitizeFilename(filename: string): string {
    // Remove any path separators and parent directory references
    return filename
      .replace(/\.\./g, '')
      .replace(/[/\\]/g, '')
      .replace(/^\.+/, '');
  }
}
